import { IconBuildingStore, IconCircleCheck } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  notifySpApiOAuthOpener,
  SP_API_OAUTH_MESSAGE_TYPE,
  type SpApiOAuthResultMessage,
  spApiSettingsLandingUrl,
} from "@/lib/sp-api-oauth";
import { sendSpApiOAuthResult } from "@/lib/sp-api-oauth-relay";

export const Route = createFileRoute("/auth/amazon/sp-api-callback")({
  validateSearch: z.object({
    sp_api: z.literal("connected").optional().catch(undefined),
    sp_api_connection: z.string().uuid().optional().catch(undefined),
    sp_api_error: z.string().max(160).optional().catch(undefined),
  }),
  component: SpApiCallbackPage,
});

function callbackResult(search: ReturnType<typeof Route.useSearch>): SpApiOAuthResultMessage {
  if (search.sp_api === "connected" && search.sp_api_connection) {
    return {
      type: SP_API_OAUTH_MESSAGE_TYPE,
      success: true,
      connectionId: search.sp_api_connection,
    };
  }
  return {
    type: SP_API_OAUTH_MESSAGE_TYPE,
    success: false,
    errorCode: search.sp_api_error ?? "sp_api.oauth.request_invalid",
  };
}

function SpApiCallbackPage(): React.ReactElement {
  const { t } = useTranslation("settings");
  const search = Route.useSearch();
  const deliveryRef = useRef<Promise<boolean> | null>(null);
  const [relayed, setRelayed] = useState(false);
  const [result] = useState(() => callbackResult(search));

  useEffect(() => {
    let disposed = false;
    let closeTimer: number | undefined;
    // Keep one delivery across StrictMode effect replays. A COOP hop can
    // detach the opener, but the per-attempt channel still reaches our tab.
    if (!deliveryRef.current) {
      deliveryRef.current = (async () => {
        try {
          if (await sendSpApiOAuthResult(result, window.sessionStorage)) return true;
        } catch {
          // Storage can be disabled independently of popup support.
        }
        if (window.opener && !window.opener.closed) {
          notifySpApiOAuthOpener(window.opener, window.location.origin, result);
          return true;
        }
        return false;
      })();
    }
    void deliveryRef.current.then((delivered) => {
      if (disposed) return;
      if (!delivered) {
        window.location.replace(spApiSettingsLandingUrl(result));
        return;
      }
      setRelayed(true);
      closeTimer = window.setTimeout(() => window.close(), 300);
    });
    return () => {
      disposed = true;
      window.clearTimeout(closeTimer);
    };
  }, [result]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <section className="flex w-full max-w-sm flex-col items-center gap-5 rounded-[var(--paper-radius-card)] border border-line bg-surface px-8 py-10 text-center shadow-sm">
        <span className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          {relayed && result.success ? (
            <IconCircleCheck aria-hidden />
          ) : relayed ? (
            <IconBuildingStore aria-hidden />
          ) : (
            <Spinner aria-hidden />
          )}
        </span>
        <div className="flex flex-col gap-1.5">
          <h1 className="text-lg font-semibold text-foreground">
            {relayed
              ? result.success
                ? t("sellerCentral.callback.success")
                : t("sellerCentral.callback.failed")
              : t("sellerCentral.callback.finishing")}
          </h1>
          <p className="text-sm leading-5 text-muted-foreground">
            {relayed ? t("sellerCentral.callback.closeSoon") : t("sellerCentral.callback.wait")}
          </p>
        </div>
        {relayed ? (
          <Button variant="outline" size="sm" onClick={() => window.close()}>
            {t("sellerCentral.callback.close")}
          </Button>
        ) : null}
      </section>
    </main>
  );
}
